"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Workspace } from "@/lib/supabase";
import {
  fetchWorkspaces,
  type WorkspacesPayload,
} from "@/lib/api/queries/workspaces";
import { STALE_TIME_DEFAULT_MS } from "@/lib/react-query/defaults";
import { queryKeys } from "@/lib/react-query/query-keys";

export type { WorkspacesPayload };

type ApiSuccess<T> = { success: true; data: T };

export function useWorkspacesQuery() {
  return useQuery({
    queryKey: queryKeys.workspaces(),
    queryFn: fetchWorkspaces,
    staleTime: STALE_TIME_DEFAULT_MS,
  });
}

export function useCreateWorkspaceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => {
      const res = await fetch("/api/workspaces", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        const err = new Error(body.error ?? "Failed to create workspace");
        (err as Error & { code?: string }).code = body.code;
        throw err;
      }
      const payload: ApiSuccess<{ workspace: Workspace }> = await res.json();
      return payload.data.workspace;
    },
    onMutate: async (name) => {
      await qc.cancelQueries({ queryKey: ["workspaces"] });
      const prev = qc.getQueryData<WorkspacesPayload>(["workspaces"]);
      const optimistic: Workspace = {
        id: `temp-${crypto.randomUUID()}`,
        user_id: "",
        name,
        is_default: false,
        is_archived: false,
        plan_locked: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      qc.setQueryData<WorkspacesPayload>(["workspaces"], (old) =>
        old
          ? { ...old, workspaces: [...old.workspaces, optimistic] }
          : { workspaces: [optimistic], active_workspace_id: null },
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["workspaces"], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["workspaces"] }),
  });
}

export function useUpdateWorkspaceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      id: string;
      name?: string;
      is_archived?: boolean;
      is_default?: boolean;
    }) => {
      const res = await fetch("/api/workspaces", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        const err = new Error(body.error ?? "Failed to update workspace");
        (err as Error & { code?: string }).code = body.code;
        throw err;
      }
      const data: ApiSuccess<{ workspace: Workspace }> = await res.json();
      return data.data.workspace;
    },
    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: ["workspaces"] });
      const prev = qc.getQueryData<WorkspacesPayload>(["workspaces"]);
      qc.setQueryData<WorkspacesPayload>(["workspaces"], (old) => {
        if (!old) return old;
        return {
          ...old,
          workspaces: old.workspaces.map((w) =>
            w.id === payload.id ? { ...w, ...payload } : w,
          ),
        };
      });
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["workspaces"], ctx.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["workspaces"] }),
  });
}

export function useDeleteWorkspaceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch("/api/workspaces", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string;
          code?: string;
        };
        const err = new Error(body.error ?? "Failed to delete workspace");
        (err as Error & { code?: string }).code = body.code;
        throw err;
      }
    },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ["workspaces"] });
      const prev = qc.getQueryData<WorkspacesPayload>(["workspaces"]);
      qc.setQueryData<WorkspacesPayload>(["workspaces"], (old) =>
        old
          ? { ...old, workspaces: old.workspaces.filter((w) => w.id !== id) }
          : old,
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["workspaces"], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      qc.invalidateQueries({ queryKey: ["scrolls"] });
      qc.invalidateQueries({ queryKey: ["folders"] });
    },
  });
}

export function useSwitchWorkspaceMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (workspace_id: string) => {
      const res = await fetch("/api/workspaces/active", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspace_id }),
      });
      if (!res.ok) throw new Error("Failed to switch workspace");
    },
    onMutate: async (workspace_id) => {
      await qc.cancelQueries({ queryKey: ["workspaces"] });
      const prev = qc.getQueryData<WorkspacesPayload>(["workspaces"]);
      qc.setQueryData<WorkspacesPayload>(["workspaces"], (old) =>
        old ? { ...old, active_workspace_id: workspace_id } : old,
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(["workspaces"], ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      // Refresh notes/notebooks scoped to the new workspace
      qc.invalidateQueries({ queryKey: ["scrolls"] });
      qc.invalidateQueries({ queryKey: ["notebooks"] });
    },
  });
}
